import axios from "axios";
import { ENV } from "../configs/environment";
import { getParams } from "../utlis/helpers";
import v5Api from "./v5Api";

const activityMonitoringApi = axios.create({
  baseURL: ENV.ACTIVITY_MONITORING_API_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// Forward user's locale so backend can return localized status/abnormality labels
activityMonitoringApi.interceptors.request.use(
  (config) => {
    const { locale } = getParams();
    config.headers["Accept-Language"] = locale || "en";
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Get sensor mapping for a senior
 * @param {string} seniorId - The senior UUID
 * @returns {Promise} API response containing sensorId if mapped
 */
export const getSensorMapping = async (seniorId) => {
  try {
    const response = await v5Api.get(`/v5/sensor/mapping?seniorId=${seniorId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching sensor mapping:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Assign a sensor to a senior
 * @param {string} seniorId - The senior UUID
 * @param {string} sensorId - The sensor ID to assign
 * @returns {Promise} API response
 */
export const assignSensorToSenior = async (seniorId, sensorId) => {
  try {
    const response = await v5Api.post(
      `/v5/sensor/mapping?seniorId=${seniorId}`,
      { sensorId },
    );
    return response.data;
  } catch (error) {
    console.error("Error assigning sensor:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Get sensor data including status, last alert, abnormality, and resident status
 * @param {string} sensorId - The sensor ID
 * @returns {Promise} API response with sensor data
 */
export const getSensorData = async (sensorId) => {
  const response = await activityMonitoringApi.get(
    `/api/v1/activity-monitoring/sensors/${sensorId}`,
  );
  return response.data;
};

/**
 * Get activity trend data for a sensor
 * @param {string} sensorId - The sensor ID
 * @param {number} from - Start timestamp in milliseconds
 * @param {number} to - End timestamp in milliseconds
 * @returns {Promise} API response with trend data
 */
export const getActivityTrend = async (sensorId, from, to) => {
  const response = await activityMonitoringApi.get(
    `/api/v1/activity-monitoring/trends/activity`,
    {
      params: { sensorId, from, to },
    },
  );
  return response.data;
};

/**
 * Get activity history (including alerts) with pagination
 * @param {string} sensorId - The sensor ID
 * @param {string} event_type - Type of event to fetch (e.g., 'alert')
 * @param {number} page - Page number (1-indexed)
 * @param {number} limit - Items per page
 * @returns {Promise} API response with paginated history
 */
export const getActivityHistory = async (
  sensorId,
  eventType,
  page = 1,
  limit = 10,
) => {
  const response = await activityMonitoringApi.get(
    `/api/v1/activity-monitoring/sensors/${sensorId}/activity`,
    {
      params: {
        event_type: eventType,
        page,
        limit,
      },
    },
  );
  return response.data;
};

/**
 * Get alert history events with pagination
 * @param {string} sensorId - The sensor ID
 * @param {number} page - Page number (1-indexed)
 * @param {number} limit - Items per page
 * @returns {Promise} API response with paginated alert events
 */
export const getAlertHistory = async (sensorId, page = 1, limit = 10) => {
  const response = await activityMonitoringApi.get(
    `/api/v1/activity-monitoring/events`,
    {
      params: {
        sn: sensorId,
        event_type: "alert",
        page,
        limit,
      },
    },
  );
  return response.data;
};

export default activityMonitoringApi;
